import { logger } from 'modules/logger';
import { env } from 'config/env';
import Retry from 'utils/retry';
import { toMediaStreamDeviceId } from 'utils/dictaphone/products/virtual';

// getUserMedia error names that indicate a transient failure (e.g. the mic is briefly held by
// another app/tab) rather than a permission problem, and are therefore worth retrying.
const RETRYABLE_GET_USER_MEDIA_ERRORS = new Set([
  'NotReadableError',
  'AbortError',
  'TrackStartError',
]);
const GET_USER_MEDIA_MAX_RETRIES = 2;
const GET_USER_MEDIA_RETRY_INITIAL_INTERVAL_MS = 500;

// By default the echoCancellation, autoGainControl, and noiseSuppression are true (i.e. enabled).
// Note that if you set echoCancellation to false but provide no values for the others, then they will be set to false (i.e. disabled).
const buildConstraints = (audioInputDeviceId: string): MediaStreamConstraints => ({
  audio: {
    deviceId: { exact: toMediaStreamDeviceId(audioInputDeviceId) },
    echoCancellation: true,
    autoGainControl: true,
    noiseSuppression: true,
  },
});

/**
 * Requests a microphone stream, resolving to `null` for every failure the caller cannot act on.
 *
 * The callbacks exist so this module stays JSX-free: the permission-denied path shows a toast
 * built from JSX with a clipboard action, which belongs to the provider.
 */
export const acquireAudioStream = async ({
  audioInputDeviceId,
  onPermissionDenied,
  onNotReady,
}: {
  audioInputDeviceId: string;
  onPermissionDenied: () => void;
  onNotReady: () => void;
}): Promise<MediaStream | null> => {
  const getUserMediaConstraints = buildConstraints(audioInputDeviceId);

  const stream = await navigator.mediaDevices
    ?.getUserMedia(getUserMediaConstraints)
    .catch(async (error: DOMException) => {
      if (env.STORYBOOK_STORYSHOTS === 'true') return;

      if (error?.name === 'NotFoundError' || error?.name === 'OverconstrainedError') {
        logger.warn(
          '[createMediaStream] Saved audio input device not found; useMicrophone will reset to default.',
          { audioInputDeviceId, errorName: error?.name }
        );
        return;
      }

      if (RETRYABLE_GET_USER_MEDIA_ERRORS.has(error?.name)) {
        logger.warn('[createMediaStream] Transient getUserMedia failure; retrying with backoff.', {
          audioInputDeviceId,
          errorName: error?.name,
        });
        try {
          return await new Retry(
            () => navigator.mediaDevices.getUserMedia(getUserMediaConstraints),
            {
              maxRetries: GET_USER_MEDIA_MAX_RETRIES,
              initialInterval: GET_USER_MEDIA_RETRY_INITIAL_INTERVAL_MS,
              backoffFn: (retries) => retries,
            }
          ).retry();
        } catch (retryError) {
          logger.warn('[createMediaStream] Exhausted retries for transient getUserMedia failure.', {
            audioInputDeviceId,
            errorName: error?.name,
            retryError,
          });
          onNotReady();
          return;
        }
      }

      onPermissionDenied();
      logger.warn(
        '[createMediaStream] Failed to get user media. Likely a user permission issue. Troubleshooting steps shown to user.',
        { errorName: error?.name }
      );
    });

  return stream ?? null;
};
